import React, { useState, useEffect, useRef } from 'react';
import { Upload, Mic, Volume2 } from 'lucide-react';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import apiService from '../api/api';

function VoiceUpload() {
  const [customVoices, setCustomVoices] = useState([]);
  const [allVoices, setAllVoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [voiceToDelete, setVoiceToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [playingVoiceId, setPlayingVoiceId] = useState(null);
  const [loadingAudio, setLoadingAudio] = useState(null);
  const voicesFetchRef = useRef(false);

  const [formData, setFormData] = useState({
    voice_name: '',
    file: null,
  });

  const builtinVoicesFetchRef = useRef(false);

  // Fetch voices on mount
  useEffect(() => {
    if (voicesFetchRef.current) return;
    voicesFetchRef.current = true;
    fetchVoices();
  }, []);

  // Update combined voices list
  useEffect(() => {
    const fetchBuiltinVoices = async () => {
      if (builtinVoicesFetchRef.current) return;
      builtinVoicesFetchRef.current = true;

      try {
        const data = await apiService.getPublicBuiltinVoices();
        const builtinVoices = (data.voices || [])
          .map((voice) => ({
            ...voice,
            isBuiltIn: true,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        const combined = [
          ...builtinVoices,
          ...customVoices,
        ];
        setAllVoices(combined);
      } catch (err) {
        console.error('Error fetching built-in voices:', err);
        // Fallback to just custom voices
        setAllVoices(customVoices);
      }
    };

    fetchBuiltinVoices();
  }, [customVoices]);

  const fetchVoices = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getCustomVoices();
      setCustomVoices(data.voices || []);
    } catch (err) {
      setError(err.detail || 'Failed to fetch voices');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const getAudioDuration = (file) => {
    return new Promise((resolve) => {
      const audio = new Audio();
      const url = URL.createObjectURL(file);
      audio.onloadedmetadata = () => {
        resolve(audio.duration);
        URL.revokeObjectURL(url);
      };
      audio.onerror = () => {
        resolve(null);
      };
      audio.src = url;
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['audio/wav', 'audio/mpeg', 'audio/mp3', 'audio/x-wav'];
      if (!validTypes.includes(file.type)) {
        setError('Please upload a valid audio file (WAV or MP3)');
        return;
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }

      // Get audio duration for validation
      const duration = await getAudioDuration(file);

      if (duration === null) {
        setError('Unable to read audio file. Please try another file.');
        return;
      }

      // Validate audio duration (10-60 seconds)
      if (duration < 10) {
        setError('Audio must be at least 10 seconds long');
        return;
      }

      if (duration > 60) {
        setError('Audio must be no longer than 60 seconds');
        return;
      }

      setError(null);
      setFormData((prev) => ({
        ...prev,
        file: file,
      }));
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setSuccess(null);
    setError(null);

    if (!formData.voice_name.trim()) {
      setError('Please provide a voice name');
      return;
    }

    if (!formData.file) {
      setError('Please select an audio file');
      return;
    }

    setUploading(true);

    try {
      const response = await apiService.uploadVoice(formData.file, formData.voice_name);

      if (response.status === 'success') {
        setSuccess('Voice uploaded successfully!');
        setFormData({ voice_name: '', file: null });
        // Reset file input
        const fileInput = document.getElementById('voice-file');
        if (fileInput) fileInput.value = '';
        // Refresh voices list
        await fetchVoices();
      }
    } catch (err) {
      setError(err.detail || 'Failed to upload voice');
    } finally {
      setUploading(false);
    }
  };

  const openDeleteModal = (voice) => {
    setVoiceToDelete(voice);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setVoiceToDelete(null);
  };

  const handleConfirmDeleteVoice = async () => {
    if (!voiceToDelete) return;

    setDeleting(voiceToDelete.id);
    try {
      const response = await apiService.deleteVoice(voiceToDelete.id);
      if (response.status === 'success') {
        setSuccess('Voice deleted successfully');
        await fetchVoices();
        closeDeleteModal();
      } else {
        setError(response.message || 'Failed to delete voice');
      }
    } catch (err) {
      setError(err.detail || 'Failed to delete voice');
    } finally {
      setDeleting(null);
    }
  };

  // Handle playing voice audio
  const handlePlayVoice = (voice) => {
    setPlayingVoiceId(voice.id);
  };

  // Handle closing audio player
  const handleCloseAudioPlayer = () => {
    setPlayingVoiceId(null);
  };

  // Filter voices based on search query (by name and tags)
  const filteredVoices = allVoices.filter((voice) => {
    const searchLower = searchQuery.toLowerCase();

    // Search by name
    if (voice.name.toLowerCase().includes(searchLower)) {
      return true;
    }

    // Search by tags (if voice has tags)
    if (voice.tags && Array.isArray(voice.tags)) {
      return voice.tags.some((tag) =>
        tag.toLowerCase().includes(searchLower)
      );
    }

    return false;
  });

  if (loading && customVoices.length === 0) {
    return (
      <div className="voice-upload-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading voices...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="voice-upload-container">
      <div className="voice-upload-grid">
        {/* LEFT COLUMN - All Voices with Search */}
        <div className="voices-column">
          <div className="voices-card">
            <div className="voices-header">
              <h2>
                <Volume2 size={20} strokeWidth={2} style={{ marginRight: '8px', display: 'inline-block' }} />
                All Voices
              </h2>
              <span className="voice-count">{filteredVoices.length}</span>
            </div>

            {/* Search Bar */}
            <div className="search-container">
              <input
                type="text"
                placeholder="🔍 Search by name, tag... (e.g., male, british, calm)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>

            {/* Voices Grid */}
            {filteredVoices.length === 0 ? (
              <div className="empty-state">
                <p className="emoji">🎤</p>
                <p>No voices found matching your search</p>
              </div>
            ) : (
              <div className="all-voices-grid">
                {filteredVoices.map((voice) => (
                  <div key={voice.id} className={`voice-list-item ${voice.isBuiltIn ? 'builtin' : 'custom'}`}>
                    <div className="voice-item-header">
                      <div className="voice-item-info">
                        <h4>{voice.name}</h4>
                        <div className="badge-tags-container">
                          {voice.isBuiltIn && <span className="badge builtin-badge">Built-in</span>}
                          {!voice.isBuiltIn && <span className="badge custom-badge">Cloned</span>}
                          {voice.tags && voice.tags.length > 0 && (
                            <div className="voice-tags-inline">
                              {voice.tags.map((tag, idx) => (
                                <span key={idx} className="tag-inline">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {voice.audio_url && (
                      <div className="voice-player-container">
                        <div className="voice-controls">
                          <button
                            className="btn-play-voice"
                            onClick={() => handlePlayVoice(voice)}
                            disabled={loadingAudio === voice.id || playingVoiceId === voice.id}
                            title="Play voice"
                          >
                            ▶ Play
                          </button>
                          {playingVoiceId === voice.id ? (
                            <>
                              <audio
                                className="voice-player-small"
                                controls
                                autoPlay
                              >
                                <source src={voice.audio_url} type="audio/wav" />
                                Your browser does not support the audio element.
                              </audio>
                              <button
                                className="btn-close-player"
                                onClick={handleCloseAudioPlayer}
                                title="Close player"
                              >
                                ✕
                              </button>
                            </>
                          ) : (
                            <div className="voice-player-dummy">
                              <div className="dummy-player-bar"></div>
                              <div className="dummy-player-lines">
                                <div className="dummy-line"></div>
                                <div className="dummy-line"></div>
                                <div className="dummy-line"></div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN - Upload & Cloned Voices */}
        <div className="actions-column">
          {/* Upload Cloned Voice Section */}
          <div className="upload-card">
            <div className="section-header">
              <h3>
                <Upload size={20} strokeWidth={2} style={{ marginRight: '8px', display: 'inline-block' }} />
                Clone Voice
              </h3>
            </div>

            {/* Guidelines Section */}
            <div className="upload-guidelines">
              <div className="guideline-item">
                <span className="guideline-icon">⏱️</span>
                <div>
                  <strong>Duration:</strong> 10-60 seconds (~ 30 secs recommended)
                </div>
              </div>
              <div className="guideline-item">
                <span className="guideline-icon">💾</span>
                <div>
                  <strong>File Size:</strong> Maximum 5MB
                </div>
              </div>
              <div className="guideline-item">
                <span className="guideline-icon">🎙️</span>
                <div>
                  <strong>Format:</strong> WAV or MP3
                </div>
              </div>
            </div>

            <form onSubmit={handleUpload} className="upload-form">
              <div className="form-group">
                <label htmlFor="voice-name">Voice Name *</label>
                <input
                  id="voice-name"
                  type="text"
                  name="voice_name"
                  value={formData.voice_name}
                  onChange={handleInputChange}
                  placeholder="e.g., Professional Voice"
                  maxLength="100"
                  disabled={uploading}
                  className="form-input"
                />
                <small className="char-count">{formData.voice_name.length}/100</small>
              </div>

              <div className="form-group">
                <label htmlFor="voice-file">Audio File (WAV/MP3, max 5MB) *</label>
                <div className="file-input-wrapper">
                  <input
                    id="voice-file"
                    type="file"
                    accept="audio/wav,audio/mpeg,audio/mp3,audio/x-wav"
                    onChange={handleFileChange}
                    disabled={uploading}
                    className="file-input"
                  />
                  <span className="file-input-label">
                    {formData.file ? formData.file.name : '📁 Choose file...'}
                  </span>
                </div>
                {formData.file && (
                  <small className="file-size">
                    Size: {(formData.file.size / 1024 / 1024).toFixed(2)} MB
                  </small>
                )}
              </div>

              {error && (
                <div className="alert alert-error">
                  <span>❌ {error}</span>
                </div>
              )}

              {success && (
                <div className="alert alert-success">
                  <span>✅ {success}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={uploading || !formData.voice_name.trim() || !formData.file}
                className="btn-upload"
              >
                {uploading ? '⏳ Uploading...' : '🚀 Upload Voice'}
              </button>
            </form>
          </div>

          {/* Your Cloned Voices Section */}
          <div className="custom-voices-card">
            <div className="section-header">
              <h3>
                <Mic size={20} strokeWidth={2} style={{ marginRight: '8px', display: 'inline-block' }} />
                Your Cloned Voices
              </h3>
              <span className="voice-count">{customVoices.length}</span>
            </div>

            {customVoices.length === 0 ? (
              <div className="empty-state compact">
                <p>No cloned voices yet</p>
                <p className="empty-subtext">Upload one above to get started</p>
              </div>
            ) : (
              <div className="custom-voices-list">
                {customVoices.map((voice) => (
                  <div key={voice.id} className="custom-voice-item">
                    <div className="voice-item-top">
                      <h4>{voice.name}</h4>
                      <button
                        className="btn-delete"
                        onClick={() => openDeleteModal(voice)}
                        disabled={deleting === voice.id}
                        title="Delete voice"
                      >
                        Delete
                      </button>
                    </div>

                    <div className="voice-item-meta">
                      <small>
                        {new Date(voice.created_at).toLocaleDateString()} at{' '}
                        {new Date(voice.created_at).toLocaleTimeString(undefined, {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </small>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {voiceToDelete && (
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          title="Delete Cloned Voice?"
          message={`Delete "${voiceToDelete.name}"?`}
          onConfirm={handleConfirmDeleteVoice}
          onCancel={closeDeleteModal}
          isLoading={deleting === voiceToDelete.id}
        />
      )}
    </div>
  );
}

export default VoiceUpload;
